import { useCallback, useEffect, useRef } from 'react';
import { closeSearchSession } from '../api/searchSessions';

export function releaseSearchSession(session?: string | null) {
  if (session) void closeSearchSession(session).catch(() => {});
}

/** Cursor history belongs to one filter/sort combination, never to a page number alone. */
export function useSearchCursor(params: URLSearchParams) {
  const query = new URLSearchParams(params);
  query.delete('page');
  query.delete('cat_label');
  query.sort();
  const key = query.toString();
  const history = useRef({ key, pages: new Map<number, string>() });
  const session = useRef<{ key: string; token: string } | null>(null);
  if (history.current.key !== key) history.current = { key, pages: new Map() };

  useEffect(() => () => {
    if (session.current?.key === key) {
      releaseSearchSession(session.current.token);
      session.current = null;
    }
  }, [key]);

  const cursorForPage = useCallback((page: number) => history.current.pages.get(page), []);
  const rememberPage = useCallback((page: number, nextCursor?: string | null, searchSession?: string | null) => {
    if (history.current.key !== key) return;
    // Starting again creates a new snapshot; its later pages must use its cursors.
    if (page === 0) {
      history.current.pages.clear();
      if (session.current?.token !== searchSession) releaseSearchSession(session.current?.token);
      session.current = null;
    }
    if (searchSession) session.current = { key, token: searchSession };
    if (nextCursor) history.current.pages.set(page + 1, nextCursor);
    else history.current.pages.delete(page + 1);
  }, [key]);
  return { cursorForPage, rememberPage };
}

export function isSearchCursorError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 422) return false;
  const detail = response.data?.detail;
  if (typeof detail === 'object' && detail != null && 'code' in detail) {
    return ['invalid_search_cursor', 'search_cursor_required'].includes(String(detail.code));
  }
  return typeof detail === 'string' && /cursor/i.test(detail);
}
